import React, { useState } from "react";
import { Skeleton } from "@/components/ui/skeleton";
import { useGetHomeQuery, useGetPlansQuery, useGetSubscriptionQuery } from "@/store/rest_api";
import { BillingInterval, SubscriptionPlan } from "@/model/interface/SubscriptionPlan";
import { PlanHeader } from "@/components/pages/plans/PlanHeader";
import { PlanIncluded } from "@/components/pages/plans/PlanIncluded";
import { PlanOptions } from "@/components/pages/plans/PlanOptions";
import { CheckoutDialog } from "@/components/pages/plans/CheckoutDialog";

interface CheckoutChoice {
  plan: SubscriptionPlan;
  interval: BillingInterval;
}

export function PlanTab() {
  const { data: user, isLoading: isLoadingUser } = useGetHomeQuery();
  const { data: plans, isLoading: isLoadingPlans } = useGetPlansQuery();
  const organisation = user?.activeOrganisation;
  const hasPlan =
    (organisation?.subscriptionStatus === "ACTIVE" || organisation?.subscriptionStatus === "PAST_DUE") &&
    !!organisation?.activePlan;
  const { data: subscription } = useGetSubscriptionQuery(undefined, {
    skip: !hasPlan || !organisation?.stripeSubscriptionId,
  });
  const [checkout, setCheckout] = useState<CheckoutChoice | null>(null);

  const currentPlan = hasPlan ? plans?.find((plan) => plan.slug === organisation?.activePlan?.toLowerCase()) : undefined;
  const currentInterval: BillingInterval = organisation?.billingInterval === "ANNUAL" ? "ANNUAL" : "MONTHLY";

  const switchToAnnual = () => {
    if (currentPlan) {
      setCheckout({ plan: currentPlan, interval: "ANNUAL" });
    }
  };

  if (isLoadingUser || isLoadingPlans || !organisation) {
    return (
      <div className="container space-y-4 p-4">
        <Skeleton className="h-16 w-full" />
        <div className="grid gap-4 md:grid-cols-3">
          <Skeleton className="h-96" />
          <Skeleton className="h-96" />
          <Skeleton className="h-96" />
        </div>
      </div>
    );
  }

  return (
    <div className="container space-y-6 p-4 text-foreground">
      <PlanHeader
        organisation={organisation}
        currentPlan={currentPlan}
        subscription={subscription}
        onSwitchToAnnual={switchToAnnual}
      />
      {currentPlan && <PlanIncluded plan={currentPlan} />}
      <PlanOptions
        plans={plans ?? []}
        currentPlan={currentPlan}
        currentInterval={currentInterval}
        onChoose={(plan, interval) => setCheckout({ plan, interval })}
      />
      {checkout && (
        <CheckoutDialog
          plan={checkout.plan}
          interval={checkout.interval}
          currentPlan={currentPlan}
          currentInterval={currentInterval}
          onClose={() => setCheckout(null)}
        />
      )}
    </div>
  );
}
